import { Divider, DropDownButton, DropdownItem, IconName } from '@optimus-web/core';
import { UserProfileResult } from '@mews-commander/data-access';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { hasCurrentProfilePlatformScope } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { useUserContext } from '../context';
import { useUserDetailContext } from '../context/userDetailContext';
import { useIsUserProvisioned } from '../hooks/useIsUserProvisioned';
import { useTableActionsInfo } from '../hooks/useTableActionsInfo';
import { UserType } from '../types';
import { handleCopyToClipboard } from '../utils';
import { useOpenEditUserDialog } from './EditUser';
import { DeleteUserDialog } from './DeleteUser';
import { useCanSeeMfaReset, ResetMFADialog } from './ResetMFA';
import { ResetPasswordDialog } from './ResetPassword';

interface UserTableActionsProps {
    userProfile: UserProfileResult;
    onSuccess?: () => void;
}

export const UserTableActions = ({ userProfile, onSuccess }: UserTableActionsProps) => {
    const { isActionDisabled: editDisabled } = useTableActionsInfo(userProfile);
    const isUserProvisioned = useIsUserProvisioned(userProfile);
    const { openUserDetail } = useUserDetailContext();
    const { userType } = useUserContext();

    const canSeeMfaReset = useCanSeeMfaReset(userProfile);
    const canSeePasswordReset = useCommanderSelector(hasCurrentProfilePlatformScope);
    const canDeleteUser = !editDisabled && !isUserProvisioned;

    const [deleteUserDialogProps, toggleDeleteUserDialog] = useDialog();
    const [resetMfaDialogProps, toggleResetMfaDialog] = useDialog();
    const [resetPasswordDialogProps, toggleResetPasswordDialog] = useDialog();

    const handleEdit = useOpenEditUserDialog({ userProfile });

    return (
        <>
            <DropDownButton icon={IconName.More} size="medium" variant="ghost">
                <DropdownItem
                    key="view"
                    label={<LocalizedText textKey={TextKey.UserActionsView} />}
                    onClick={() => openUserDetail({ userId: userProfile.userId })}
                />
                {editDisabled ? null : (
                    <DropdownItem
                        key="edit"
                        label={<LocalizedText textKey={TextKey.UserActionsEdit} />}
                        onClick={handleEdit}
                    />
                )}
                {userType === UserType.Properties && !editDisabled ? (
                    <DropdownItem
                        key="properties"
                        label={<LocalizedText textKey={TextKey.UserActionsProperties} />}
                        onClick={() => openUserDetail({ userId: userProfile.userId, properties: true })}
                    />
                ) : null}
                {canSeeMfaReset || userType !== UserType.Properties ? <Divider /> : null}
                {userType !== UserType.Properties && (
                    <DropdownItem
                        key="copyId"
                        label={<LocalizedText textKey={TextKey.CopyProfileId} />}
                        onClick={() => handleCopyToClipboard(userProfile.enterpriseProfileIds?.[0])}
                    />
                )}
                {canSeeMfaReset && (
                    <DropdownItem
                        key="resetMfa"
                        label={<LocalizedText textKey={TextKey.ResetTwoFactorShort} />}
                        onClick={toggleResetMfaDialog}
                    />
                )}
                {canSeePasswordReset && (
                    <DropdownItem
                        key="resetPassword"
                        label={<LocalizedText textKey={TextKey.ResetPassword} />}
                        onClick={toggleResetPasswordDialog}
                    />
                )}
                {canDeleteUser && <Divider />}
                {canDeleteUser && (
                    <DropdownItem
                        key="remove"
                        label={<LocalizedText textKey={TextKey.UserActionsRemove} />}
                        onClick={toggleDeleteUserDialog}
                        variant="danger"
                    />
                )}
            </DropDownButton>

            {canSeeMfaReset && <ResetMFADialog userId={userProfile.userId} {...resetMfaDialogProps} />}
            {canSeePasswordReset && <ResetPasswordDialog email={userProfile.email} {...resetPasswordDialogProps} />}
            {canDeleteUser && (
                <DeleteUserDialog userProfile={userProfile} onSuccess={onSuccess} {...deleteUserDialogProps} />
            )}
        </>
    );
};
